import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { DomainBiomassDay } from '@/domain/cages/overview.types';
import type { Locale } from '@/utils/localeFormatters';

import { daypartColor } from '../daypartSeries';

// ---------------------------------------------------------------------------
// Size — fish height and length
// ---------------------------------------------------------------------------

export interface SizeChartSpecParams {
    days: DomainBiomassDay[];
    /** Already translated. Left axis. */
    heightLabel: string;
    /** Already translated. Right axis. */
    lengthLabel: string;
    locale: Locale;
}

/** Morphometry is reported in millimetres and displayed in centimetres. */
const MM_PER_CM = 10;
const SIZE_TICK_DECIMALS = 1;
const SIZE_VALUE_DECIMALS = 1;
const SIZE_UNIT = 'cm';
/** Step sizes in cm, converted from the legacy chart's 2 mm and 10 mm. */
const HEIGHT_STEP_SIZE = 0.2;
const LENGTH_STEP_SIZE = 1;
/** The legacy mapper hardcoded this daypart, ignoring the page filter. */
const SIZE_DAYPART_TAG = 'all';

function millimetresToCentimetres(value: number | null): number | null {
    return value === null ? null : value / MM_PER_CM;
}

/**
 * Fish height (left) and length (right), both in centimetres.
 *
 * The API reports millimetres; the conversion happens here rather than in the
 * domain mapper because the cage dashboard's CSV export ships these fields raw
 * and would otherwise silently change units.
 *
 * The legacy chart converted for display with an axis multiplier and had its two
 * `formatAxis` tick types crossed — the right axis, which carries length, was
 * configured as height and vice versa. Declaring each axis' own label and unit
 * removes that class of mistake, and corrects it here.
 */
export function buildSizeChartSpec(params: SizeChartSpecParams): MetricChartSpec<'line'> {
    const { days, heightLabel, lengthLabel, locale } = params;
    const sizeDays = days.filter((day) => day.daypartTag === SIZE_DAYPART_TAG);

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [
            {
                key: 'fishHeightAvg',
                label: heightLabel,
                // Its own palette entry rather than a daypart colour: this chart
                // plots two metrics, not two dayparts. Height green, length blue.
                color: graphSettings.heightLengthColors.height,
                data: sizeDays.map((day) => ({
                    day: day.day,
                    value: millimetresToCentimetres(day.data.fishHeightAvg),
                })),
            },
            {
                key: 'fishLengthAvg',
                label: lengthLabel,
                color: graphSettings.heightLengthColors.length,
                axis: 'right',
                data: sizeDays.map((day) => ({
                    day: day.day,
                    value: millimetresToCentimetres(day.data.fishLengthAvg),
                })),
            },
        ],
        left: {
            label: heightLabel,
            unit: SIZE_UNIT,
            stepSize: HEIGHT_STEP_SIZE,
            tickDecimals: SIZE_TICK_DECIMALS,
            valueDecimals: SIZE_VALUE_DECIMALS,
        },
        right: {
            label: lengthLabel,
            unit: SIZE_UNIT,
            stepSize: LENGTH_STEP_SIZE,
            tickDecimals: SIZE_TICK_DECIMALS,
            valueDecimals: SIZE_VALUE_DECIMALS,
        },
        locale,
    };
}

// ---------------------------------------------------------------------------
// Condition factor
// ---------------------------------------------------------------------------

export interface ConditionFactorChartSpecParams {
    days: DomainBiomassDay[];
    axisLabel: string;
    locale: Locale;
}

/** The whole plotted range sits between roughly 1.1 and 1.5. */
const CF_SUGGESTED_MIN = 1.1;
const CF_SUGGESTED_MAX = 1.5;
const CF_DECIMALS = 2;
/** The legacy mapper hardcoded this daypart, ignoring the page filter. */
const CF_DAYPART_TAG = 'all';

/**
 * Condition factor (K-factor) for the `all` daypart.
 *
 * A single series, not a daypart fan-out — the legacy mapper took no daypart
 * arguments at all and always plotted `all`.
 *
 * `tickDecimals` has to be 2 here: the values span about 1.31 to 1.36, so
 * rounding ticks to whole numbers would label every one of them "1".
 */
export function buildConditionFactorChartSpec(
    params: ConditionFactorChartSpecParams
): MetricChartSpec<'line'> {
    const { days, axisLabel, locale } = params;

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [
            {
                key: 'cfAvg',
                label: axisLabel,
                color: daypartColor('all'),
                data: days
                    .filter((day) => day.daypartTag === CF_DAYPART_TAG)
                    .map((day) => ({ day: day.day, value: day.data.cfAvg })),
            },
        ],
        left: {
            label: axisLabel,
            suggestedMin: CF_SUGGESTED_MIN,
            suggestedMax: CF_SUGGESTED_MAX,
            tickDecimals: CF_DECIMALS,
            valueDecimals: CF_DECIMALS,
        },
        locale,
    };
}
